// Modèles de notifications — /notifications-settings. The e-mails sent to the
// tenant's customers at each event (ticket created, reply published…),
// grouped by event type: the axis the backend resolves templates on at send
// time (notifications_service._find_notification_settings: active only;
// brand + language → brand + EN → account + language → account + EN →
// platform default e-mail). Filters (q, lang, brand) live in the URL.
// API: GET/POST/PUT/DELETE /api/notifications/html-content,
// GET /api/notifications/notification-types, GET /api/brands/list.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  BellRing, Clock, Code, Eye, Languages, Mail, Pencil, Plus, Search, Trash2, X,
} from 'lucide-react';
import { AttentionCard, authHeaders, formatDay, scrollToId } from './ChannelsKit';
import {
  Badge, Banner, Card, ConfirmDialog, Field, Kpi, Modal, OverflowMenu, PageHeader, Switch, Tabs, Toast,
} from '../design/ui';
import './NotificationsSettings.css';

const DEFAULT_LANGUAGE = 'EN';

const normalizeLanguageValue = (language) => (language ? language.trim().toUpperCase() : DEFAULT_LANGUAGE);

// The five backend event types (NotificationType enum), in display order.
const TYPE_ORDER = ['ticket_created', 'ticket_created_by_mail', 'response_received', 'outlook_reply', 'client_email'];

const emptyForm = () => ({
  name: '',
  notification_type: '',
  subject: '',
  html_content: '',
  description: '',
  is_active: true,
  language: DEFAULT_LANGUAGE,
  brand_id: '',
});

// Event types to show: the backend's, plus any type present in the data so
// nothing is ever silently dropped.
export const orderedTypes = (declared, list) => {
  const known = new Set([...TYPE_ORDER, ...(declared || []), ...(list || []).map((n) => n.notification_type)]);
  return [...TYPE_ORDER.filter((type) => known.has(type)), ...[...known].filter((type) => !TYPE_ORDER.includes(type)).sort()];
};

// State in numbers, from the list the API returned.
export const templateStats = (list, types) => {
  const active = list.filter((n) => n.is_active);
  const languages = [...new Set(list.map((n) => n.language))].sort();
  const latest = list.reduce((best, n) => (!best || String(n.updated_at || '') > String(best.updated_at || '') ? n : best), null);
  return {
    total: list.length,
    active: active.length,
    customTypes: types.filter((type) => active.some((n) => n.notification_type === type)).length,
    languages,
    latest,
  };
};

// Two active templates for the same event, language and brand: the send
// path takes the first one Mongo returns (find_one), so one never leaves.
export const duplicateGroups = (list) => {
  const groups = {};
  list.filter((n) => n.is_active).forEach((n) => {
    const key = `${n.notification_type}|${n.language}|${n.brand_id || ''}`;
    groups[key] = [...(groups[key] || []), n];
  });
  return Object.values(groups)
    .filter((group) => group.length > 1)
    .map((group) => ({
      type: group[0].notification_type, language: group[0].language, brandId: group[0].brand_id || null, templates: group,
    }));
};

const NotificationsSettings = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const [searchParams, setSearchParams] = useSearchParams();

  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState(null); // null = loading
  const [loadFailed, setLoadFailed] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [toast, setToast] = useState(null);
  // modal = { editing, form, error, saving, view: 'code' | 'preview' }
  const [modal, setModal] = useState(null);
  // deleteModal = { notification, deleting }
  const [deleteModal, setDeleteModal] = useState(null);
  const [preview, setPreview] = useState(null);
  const [togglingIds, setTogglingIds] = useState([]);
  const toastTimer = useRef(null);

  const searchTerm = searchParams.get('q') || '';
  const languageFilter = searchParams.get('lang') || '';
  const brandFilter = searchParams.get('brand') || '';

  const updateParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace: true });
  };

  const showToast = useCallback((tone, text) => {
    clearTimeout(toastTimer.current);
    setToast({ tone, text });
    if (tone === 'success') toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get('/api/notifications/html-content', { headers: authHeaders() });
      setNotifications((response.data || []).map((n) => ({ ...n, language: normalizeLanguageValue(n.language) })));
      setLoadFailed(false);
    } catch (error) {
      setNotifications([]);
      setLoadFailed(true);
      showToast('danger', t('notificationsPage.fetchError', 'Impossible de charger les modèles de notifications.'));
    }
  }, [showToast, t]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    axios.get('/api/users/user', { headers })
      .then((res) => {
        setUser(res.data);
        if (res.data?.has_brand) {
          axios.get('/api/brands/list', { headers })
            .then((r) => setBrands(r.data || []))
            .catch(() => setBrands([]));
        }
      })
      .catch(() => navigate('/'));
    axios.get('/api/notifications/notification-types', { headers })
      .then((res) => setNotificationTypes(res.data || []))
      .catch(() => setNotificationTypes([]));
    fetchNotifications();
  }, [navigate, fetchNotifications]);

  const canManage = Boolean(user && ['admin', 'seller', 'eshop'].includes(user.role));
  const showBrands = Boolean(user?.has_brand && brands.length > 0);

  // ------------------------------------------------------------ metadata

  const TYPE_META = {
    ticket_created: {
      label: t('notificationsPage.typeTicketCreated', 'Ticket créé'),
      hint: t('notificationsPage.typeTicketCreatedHint', 'envoyé au client quand un ticket est créé depuis le site ou un formulaire'),
    },
    ticket_created_by_mail: {
      label: t('notificationsPage.typeTicketCreatedByMail', 'Ticket créé par email'),
      hint: t('notificationsPage.typeTicketCreatedByMailHint', 'accusé de réception envoyé quand un email entrant ouvre un ticket'),
    },
    response_received: {
      label: t('notificationsPage.typeResponseReceived', 'Réponse reçue'),
      hint: t('notificationsPage.typeResponseReceivedHint', 'envoyé au client quand une réponse est publiée sur son ticket'),
    },
    outlook_reply: {
      label: t('notificationsPage.typeOutlookReply', 'Réponse Outlook'),
      hint: t('notificationsPage.typeOutlookReplyHint', 'habillage des réponses envoyées via une boîte Outlook connectée'),
    },
    client_email: {
      label: t('notificationsPage.typeClientEmail', 'Email client'),
      hint: t('notificationsPage.typeClientEmailHint', 'habillage des emails envoyés directement au client'),
    },
  };
  const typeLabel = (type) => TYPE_META[type]?.label || type;

  const getBrandName = (brandId) => {
    if (!brandId) return t('notificationsPage.userLevel', 'Niveau utilisateur');
    const brand = brands.find((b) => b.id === brandId);
    return brand ? brand.name : brandId;
  };

  const list = useMemo(() => notifications || [], [notifications]);
  const allTypes = useMemo(() => orderedTypes(notificationTypes, list), [notificationTypes, list]);
  const stats = useMemo(() => templateStats(list, allTypes), [list, allTypes]);
  const duplicates = useMemo(() => duplicateGroups(list), [list]);
  const duplicateIds = new Set(duplicates.flatMap((group) => group.templates.map((n) => n.id)));
  const languageOptions = [...new Set([DEFAULT_LANGUAGE, ...list.map((n) => n.language)])].sort();

  // ------------------------------------------------------------ filtering

  const q = searchTerm.toLowerCase();
  const matchesFilters = (notification) => {
    if (languageFilter && notification.language !== languageFilter) return false;
    if (brandFilter === 'user-level' && notification.brand_id) return false;
    if (brandFilter && brandFilter !== 'user-level' && notification.brand_id !== brandFilter) return false;
    if (q) {
      const hay = `${notification.name} ${notification.subject} ${notification.description || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };
  const hasActiveFilters = Boolean(searchTerm || languageFilter || brandFilter);
  const shownCount = list.filter(matchesFilters).length;

  // ------------------------------------------------------------ actions

  const openCreateModal = (presetType = '') => {
    if (!canManage) return;
    setModal({ editing: null, form: { ...emptyForm(), notification_type: presetType }, error: null, saving: false, view: 'code' });
  };

  const openEditModal = (notification) => {
    if (!canManage) return;
    setModal({
      editing: notification,
      form: {
        name: notification.name,
        notification_type: notification.notification_type,
        subject: notification.subject,
        html_content: notification.html_content,
        description: notification.description || '',
        is_active: notification.is_active,
        language: notification.language,
        brand_id: notification.brand_id || '',
      },
      error: null,
      saving: false,
      view: 'code',
    });
  };

  const setForm = (patch) => setModal((m) => ({ ...m, form: { ...m.form, ...patch }, error: null }));

  const handleSubmit = async () => {
    const { form, editing } = modal;
    if (!form.name.trim() || !form.notification_type || !form.subject.trim() || !form.html_content.trim()) {
      setModal((m) => ({ ...m, error: t('notificationsPage.requiredFields', 'Nom, événement, objet et contenu HTML sont requis.') }));
      return;
    }
    setModal((m) => ({ ...m, saving: true, error: null }));
    const payload = { ...form, language: normalizeLanguageValue(form.language) };
    try {
      if (editing) {
        await axios.put(`/api/notifications/html-content/${editing.id}`, payload, { headers: authHeaders() });
        showToast('success', t('notificationsPage.updated', 'Modèle mis à jour.'));
      } else {
        await axios.post('/api/notifications/html-content', payload, { headers: authHeaders() });
        showToast('success', t('notificationsPage.created', 'Modèle créé.'));
      }
      setModal(null);
      fetchNotifications();
    } catch (error) {
      setModal((m) => ({
        ...m,
        saving: false,
        error: error.response?.data?.detail || t('notificationsPage.saveError', 'Échec de l’enregistrement du modèle.'),
      }));
    }
  };

  const confirmDelete = async () => {
    setDeleteModal((m) => ({ ...m, deleting: true }));
    try {
      await axios.delete(`/api/notifications/html-content/${deleteModal.notification.id}`, { headers: authHeaders() });
      setDeleteModal(null);
      showToast('success', t('notificationsPage.deleted', 'Modèle supprimé.'));
      fetchNotifications();
    } catch (error) {
      setDeleteModal(null);
      showToast('danger', error.response?.data?.detail || t('notificationsPage.deleteError', 'Échec de la suppression du modèle.'));
    }
  };

  const toggleActiveStatus = async (notification) => {
    if (!canManage) return;
    setTogglingIds((prev) => [...prev, notification.id]);
    try {
      await axios.put(`/api/notifications/html-content/${notification.id}`, { is_active: !notification.is_active }, { headers: authHeaders() });
      await fetchNotifications();
      showToast('success', notification.is_active
        ? t('channelsUi.notifications.deactivated', '« {{name}} » est désactivé : il ne part plus.', { name: notification.name })
        : t('channelsUi.notifications.activated', '« {{name}} » est actif.', { name: notification.name }));
    } catch (error) {
      showToast('danger', t('notificationsPage.toggleError', 'Échec de l’activation/désactivation du modèle.'));
    } finally {
      setTogglingIds((prev) => prev.filter((id) => id !== notification.id));
    }
  };

  // ------------------------------------------------------------ render

  const loading = notifications === null;
  const scopeLabel = (n) => [n.language, showBrands ? getBrandName(n.brand_id) : null].filter(Boolean).join(' · ');

  const header = (
    <PageHeader
      title={t('notificationsPage.title', 'Modèles de notifications')}
      subtitle={!loading && stats.total > 0
        ? t('channelsUi.notifications.subtitleCount', '{{active}} modèles actifs sur {{total}} · {{custom}} événements sur {{types}} personnalisés', {
          total: stats.total, active: stats.active, custom: stats.customTypes, types: allTypes.length,
        })
        : t('channelsUi.notifications.subtitle', 'Les e-mails que vos clients reçoivent à chaque étape de leur demande.')}
      actions={canManage ? (
        <button type="button" className="u-btn u-btn--primary" onClick={() => openCreateModal()}>
          <Plus aria-hidden="true" />
          {t('notificationsPage.create', 'Créer un modèle')}
        </button>
      ) : null}
    />
  );

  if (loading) {
    return (
      <div className="u-page u-page--narrow notif-v2" aria-busy="true">
        {header}
        <div className="u-well">
          <div className="u-kpi-row">{[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton notif-v2__kpi-skeleton" />)}</div>
          <span className="u-skeleton notif-v2__list-skeleton" />
        </div>
      </div>
    );
  }

  return (
    <div className="u-page u-page--narrow notif-v2">
      {header}

      <div className="u-well">
        <AttentionCard
          title={t('channelsUi.attention', 'À vérifier')}
          criticalLabel={t('channelsUi.critical', 'Bloquant')}
          watchLabel={t('channelsUi.watch', 'À surveiller')}
          items={duplicates.map((group) => ({
            key: `${group.type}-${group.language}-${group.brandId || ''}`,
            tone: 'warning',
            title: t('channelsUi.notifications.duplicateTitle', '{{count}} modèles actifs pour « {{type}} » · {{scope}}', {
              count: group.templates.length,
              type: typeLabel(group.type),
              scope: [group.language, getBrandName(group.brandId)].join(' · '),
            }),
            detail: t('channelsUi.notifications.duplicateDetail', 'Un seul part à l’envoi, sans ordre garanti : {{names}}. Désactivez celui qui ne doit plus servir.', {
              names: group.templates.map((n) => `« ${n.name} »`).join(', '),
            }),
            action: { label: t('channelsUi.notifications.openType', 'Voir les modèles'), onClick: () => scrollToId(`notif-type-${group.type}`) },
          }))}
        />

        {loadFailed && <Banner tone="danger">{t('notificationsPage.fetchError', 'Impossible de charger les modèles de notifications.')}</Banner>}

        {stats.total > 0 ? (
          <div className="u-kpi-row">
            <Kpi
              icon={Mail}
              label={t('channelsUi.notifications.kpiActive', 'Modèles actifs')}
              value={stats.active}
              unit={`/ ${stats.total}`}
              meter={{ value: stats.active, max: stats.total, label: t('channelsUi.notifications.kpiActive', 'Modèles actifs') }}
              context={stats.total - stats.active > 0
                ? t('channelsUi.notifications.kpiInactive', '{{count}} désactivés : jamais envoyés', { count: stats.total - stats.active })
                : t('channelsUi.notifications.kpiAllActive', 'tous utilisables à l’envoi')}
            />
            <Kpi
              icon={BellRing}
              tone="info"
              label={t('channelsUi.notifications.kpiTypes', 'Événements personnalisés')}
              value={stats.customTypes}
              unit={`/ ${allTypes.length}`}
              context={stats.customTypes < allTypes.length
                ? t('channelsUi.notifications.kpiTypesRest', 'les autres envoient l’e-mail par défaut')
                : t('channelsUi.notifications.kpiTypesAll', 'chaque événement a son modèle')}
            />
            <Kpi
              icon={Languages}
              tone="neutral"
              label={t('channelsUi.notifications.kpiLanguages', 'Langues')}
              value={stats.languages.length}
              context={stats.languages.join(' · ')}
            />
            {stats.latest?.updated_at && (
              <Kpi
                icon={Clock}
                tone="neutral"
                label={t('channelsUi.notifications.kpiLatest', 'Dernière modification')}
                value={formatDay(stats.latest.updated_at, lang)}
                context={`« ${stats.latest.name} »`}
              />
            )}
          </div>
        ) : !loadFailed && (
          <Banner tone="info" title={t('channelsUi.notifications.emptyTitle', 'Aucun modèle personnalisé')}>
            {t('channelsUi.notifications.emptyText', 'Vos clients reçoivent les e-mails par défaut de la plateforme. Créez un modèle pour un événement afin de l’habiller à vos couleurs et dans vos mots.')}
          </Banner>
        )}

        <Card
          icon={Mail}
          title={t('channelsUi.notifications.listTitle', 'Modèles par événement')}
          description={t('channelsUi.notifications.listDesc', 'À l’envoi, Unanimity prend le modèle actif de la marque dans la langue du client, sinon en anglais, sinon celui du niveau utilisateur ; à défaut, l’e-mail par défaut de la plateforme.')}
          flush
        >
          <div className="u-toolbar">
            <div className="u-input-group u-input-group--lead">
              <Search className="u-input-group__icon" aria-hidden="true" />
              <input
                className="u-input"
                value={searchTerm}
                onChange={(e) => updateParams({ q: e.target.value })}
                placeholder={t('notificationsPage.searchPlaceholder', 'Rechercher un modèle, un objet…')}
                aria-label={t('notificationsPage.searchPlaceholder', 'Rechercher un modèle, un objet…')}
              />
              {searchTerm && (
                <button type="button" className="u-input-group__btn" onClick={() => updateParams({ q: null })} aria-label={t('channelsUi.clearSearch', 'Effacer la recherche')} title={t('channelsUi.clearSearch', 'Effacer la recherche')}>
                  <X aria-hidden="true" />
                </button>
              )}
            </div>
            <select className="u-select" value={languageFilter} onChange={(e) => updateParams({ lang: e.target.value })} aria-label={t('notificationsPage.language', 'Langue')}>
              <option value="">{t('channelsUi.notifications.allLanguages', 'Toutes les langues')}</option>
              {languageOptions.map((language) => <option key={language} value={language}>{language}</option>)}
            </select>
            {showBrands && (
              <select className="u-select" value={brandFilter} onChange={(e) => updateParams({ brand: e.target.value })} aria-label={t('notificationsPage.brand', 'Marque')}>
                <option value="">{t('channelsUi.notifications.allBrands', 'Toutes les marques')}</option>
                <option value="user-level">{t('notificationsPage.userLevel', 'Niveau utilisateur')}</option>
                {brands.map((brand) => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
              </select>
            )}
            <span className="u-toolbar__spacer" />
            {hasActiveFilters && (
              <>
                <span className="notif-v2__shown u-num">
                  {t('channelsUi.notifications.shown', '{{count}} modèles affichés sur {{total}}', { count: shownCount, total: stats.total })}
                </span>
                <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => updateParams({ q: null, lang: null, brand: null })}>
                  {t('channelsUi.clearFilters', 'Effacer les filtres')}
                </button>
              </>
            )}
          </div>

          {allTypes.map((type) => {
            const templates = list.filter((n) => n.notification_type === type);
            const visible = templates.filter(matchesFilters);
            const activeCount = templates.filter((n) => n.is_active).length;
            return (
              <section key={type} className="notif-v2__group" id={`notif-type-${type}`} aria-labelledby={`notif-type-${type}-title`}>
                <div className="notif-v2__group-head">
                  <div className="notif-v2__group-text">
                    <h3 id={`notif-type-${type}-title`}>
                      {typeLabel(type)}
                      {templates.length > 0 && (
                        <Badge tone={activeCount ? 'success' : undefined} dot={activeCount > 0}>
                          {t('notificationsPage.typeCount', '{{active}} actifs / {{total}}', { active: activeCount, total: templates.length })}
                        </Badge>
                      )}
                    </h3>
                    {TYPE_META[type]?.hint && <p>{TYPE_META[type].hint}</p>}
                  </div>
                  {canManage && (
                    <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openCreateModal(type)}>
                      <Plus aria-hidden="true" />
                      {t('notificationsPage.addTemplate', 'Ajouter')}
                    </button>
                  )}
                </div>

                {templates.length === 0 ? (
                  <p className="notif-v2__group-empty">
                    {t('notificationsPage.noTemplatesForType', 'Aucun modèle personnalisé — l’email par défaut de la plateforme est utilisé.')}
                  </p>
                ) : visible.length === 0 ? (
                  <p className="notif-v2__group-empty">
                    {t('notificationsPage.noMatchesForType', 'Aucun modèle ne correspond aux filtres pour cet événement.')}
                  </p>
                ) : (
                  <ul className="u-list notif-v2__rows">
                    {visible.map((notification) => {
                      const meta = [
                        notification.subject,
                        notification.updated_at && t('channelsUi.notifications.modified', 'modifié le {{date}}', { date: formatDay(notification.updated_at, lang) }),
                        notification.created_by && t('notificationsPage.metaBy', 'par {{author}}', { author: notification.created_by }),
                      ].filter(Boolean).join(' · ');
                      return (
                        <li key={notification.id} className={`u-row notif-v2__row${notification.is_active ? '' : ' is-inactive'}`}>
                          {canManage && (
                            <Switch
                              id={`notif-active-${notification.id}`}
                              checked={notification.is_active}
                              disabled={togglingIds.includes(notification.id)}
                              onChange={() => toggleActiveStatus(notification)}
                            />
                          )}
                          <div className="u-row__main">
                            <div className="u-row__title">
                              <button
                                type="button"
                                className="notif-v2__name"
                                onClick={() => (canManage ? openEditModal(notification) : setPreview(notification))}
                                title={canManage ? t('notificationsPage.edit', 'Modifier') : t('notificationsPage.preview', 'Aperçu')}
                              >
                                {notification.name}
                              </button>
                              <span className="u-code">{notification.language}</span>
                              {showBrands && <Badge tone={notification.brand_id ? 'info' : undefined}>{getBrandName(notification.brand_id)}</Badge>}
                              {!notification.is_active && <Badge>{t('channelsUi.notifications.inactive', 'Désactivé')}</Badge>}
                              {duplicateIds.has(notification.id) && <Badge tone="warning" dot>{t('channelsUi.notifications.duplicate', 'En double')}</Badge>}
                            </div>
                            <div className="u-row__meta" title={meta}>{meta}</div>
                          </div>
                          <div className="u-row__side">
                            <button
                              type="button"
                              className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                              onClick={() => setPreview(notification)}
                              title={t('notificationsPage.preview', 'Aperçu')}
                              aria-label={t('channelsUi.notifications.previewOf', 'Aperçu de « {{name}} »', { name: notification.name })}
                            >
                              <Eye aria-hidden="true" />
                            </button>
                            {canManage && (
                              <OverflowMenu
                                label={t('channelsUi.moreActions', 'Plus d’actions')}
                                items={[
                                  { label: t('notificationsPage.edit', 'Modifier'), icon: Pencil, onClick: () => openEditModal(notification) },
                                  { separator: true },
                                  { label: t('notificationsPage.delete', 'Supprimer'), icon: Trash2, danger: true, onClick: () => setDeleteModal({ notification, deleting: false }) },
                                ]}
                              />
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </Card>
      </div>

      {modal && (
        <Modal
          wide
          icon={Mail}
          title={modal.editing ? t('notificationsPage.editTitle', 'Modifier le modèle') : t('notificationsPage.createTitle', 'Créer un modèle')}
          description={modal.form.notification_type && TYPE_META[modal.form.notification_type]?.hint
            ? `${typeLabel(modal.form.notification_type)} : ${TYPE_META[modal.form.notification_type].hint}`
            : null}
          onClose={() => setModal(null)}
          closeLabel={t('common.close', 'Fermer')}
          footer={(
            <>
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setModal(null)} disabled={modal.saving}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="button" className="u-btn u-btn--primary" onClick={handleSubmit} disabled={modal.saving}>
                {modal.saving
                  ? t('common.saving', 'Enregistrement...')
                  : modal.editing ? t('common.save', 'Enregistrer') : t('notificationsPage.submitCreate', 'Créer le modèle')}
              </button>
            </>
          )}
        >
          <div className="u-form">
            <div className="u-form-grid">
              <Field label={`${t('notificationsPage.templateName', 'Nom du modèle')} *`} htmlFor="nt-name">
                {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                <input id="nt-name" className="u-input" maxLength={100} value={modal.form.name} onChange={(e) => setForm({ name: e.target.value })} autoFocus />
              </Field>
              <Field label={`${t('notificationsPage.eventType', 'Type d’événement')} *`} htmlFor="nt-type">
                <select id="nt-type" className="u-select" value={modal.form.notification_type} onChange={(e) => setForm({ notification_type: e.target.value })}>
                  <option value="">{t('notificationsPage.selectType', 'Sélectionner…')}</option>
                  {allTypes.map((type) => <option key={type} value={type}>{typeLabel(type)}</option>)}
                </select>
              </Field>
              <Field
                label={`${t('notificationsPage.language', 'Langue')} *`}
                htmlFor="nt-lang"
                help={t('notificationsPage.scopeHint', 'Un modèle de marque prime sur le niveau utilisateur ; la langue EN sert de repli.')}
              >
                <input
                  id="nt-lang"
                  className="u-input"
                  maxLength={10}
                  value={modal.form.language}
                  onChange={(e) => setForm({ language: e.target.value.toUpperCase() })}
                  placeholder="FR"
                  list="nt-language-options"
                />
                <datalist id="nt-language-options">
                  {languageOptions.map((language) => <option key={language} value={language} />)}
                </datalist>
              </Field>
              {showBrands && (
                <Field label={t('notificationsPage.brand', 'Marque')} htmlFor="nt-brand">
                  <select id="nt-brand" className="u-select" value={modal.form.brand_id} onChange={(e) => setForm({ brand_id: e.target.value })}>
                    <option value="">{t('notificationsPage.userLevel', 'Niveau utilisateur')}</option>
                    {brands.map((brand) => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
                  </select>
                </Field>
              )}
            </div>
            <Field label={`${t('notificationsPage.emailSubject', 'Objet de l’email')} *`} htmlFor="nt-subject">
              <input id="nt-subject" className="u-input" maxLength={200} value={modal.form.subject} onChange={(e) => setForm({ subject: e.target.value })} />
            </Field>
            <Field label={t('notificationsPage.description', 'Description')} htmlFor="nt-desc">
              <input
                id="nt-desc"
                className="u-input"
                maxLength={500}
                value={modal.form.description}
                onChange={(e) => setForm({ description: e.target.value })}
                placeholder={t('notificationsPage.descriptionPlaceholder', 'Optionnelle — note interne')}
              />
            </Field>
            <div className="u-field">
              <div className="notif-v2__html-head">
                <label className="u-label" htmlFor="nt-html">{`${t('notificationsPage.htmlContent', 'Contenu HTML')} *`}</label>
                <Tabs
                  label={t('notificationsPage.htmlContent', 'Contenu HTML')}
                  active={modal.view}
                  onChange={(view) => setModal((m) => ({ ...m, view }))}
                  tabs={[
                    { key: 'code', label: t('notificationsPage.viewCode', 'Code'), icon: Code },
                    { key: 'preview', label: t('notificationsPage.viewPreview', 'Aperçu'), icon: Eye },
                  ]}
                />
              </div>
              {modal.view === 'code' ? (
                <textarea
                  id="nt-html"
                  className="u-textarea notif-v2__code"
                  rows={12}
                  value={modal.form.html_content}
                  onChange={(e) => setForm({ html_content: e.target.value })}
                  placeholder={t('notificationsPage.htmlPlaceholder', 'Contenu HTML de l’email…')}
                  spellCheck={false}
                />
              ) : modal.form.html_content.trim() ? (
                <div className="notif-v2__frame">
                  <iframe srcDoc={modal.form.html_content} title={t('notificationsPage.viewPreview', 'Aperçu')} sandbox="" />
                </div>
              ) : (
                <p className="u-help">{t('notificationsPage.previewEmpty', 'Aucun contenu HTML à prévisualiser.')}</p>
              )}
            </div>
            <Switch
              id="nt-active"
              checked={modal.form.is_active}
              onChange={(value) => setForm({ is_active: value })}
              label={modal.form.is_active
                ? t('notificationsPage.activeLabel', 'Actif — utilisable dès l’enregistrement')
                : t('notificationsPage.inactiveLabel', 'Inactif — jamais sélectionné à l’envoi')}
            />
            {modal.error && <Banner tone="danger">{modal.error}</Banner>}
          </div>
        </Modal>
      )}

      {deleteModal && (
        <ConfirmDialog
          title={t('channelsUi.notifications.deleteTitle', 'Supprimer le modèle « {{name}} » ?', { name: deleteModal.notification.name })}
          message={t('channelsUi.notifications.deleteMessage', '« {{type}} » · {{scope}} : ce modèle ne partira plus. S’il n’en reste aucun actif pour ce cas, vos clients reçoivent le modèle de repli ou l’e-mail par défaut. Cette action est irréversible.', {
            type: typeLabel(deleteModal.notification.notification_type),
            scope: scopeLabel(deleteModal.notification),
          })}
          confirmLabel={t('notificationsPage.delete', 'Supprimer')}
          cancelLabel={t('common.cancel', 'Annuler')}
          icon={Trash2}
          busy={deleteModal.deleting}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteModal(null)}
        />
      )}

      {preview && (
        <Modal
          wide
          icon={Eye}
          title={preview.name}
          description={`${t('notificationsPage.emailSubject', 'Objet de l’email')} : ${preview.subject}`}
          onClose={() => setPreview(null)}
          closeLabel={t('common.close', 'Fermer')}
          footer={canManage ? (
            <button type="button" className="u-btn u-btn--secondary" onClick={() => { const n = preview; setPreview(null); openEditModal(n); }}>
              <Pencil aria-hidden="true" />
              {t('notificationsPage.edit', 'Modifier')}
            </button>
          ) : null}
        >
          <div className="notif-v2__preview-meta">
            <Badge>{typeLabel(preview.notification_type)}</Badge>
            <span className="u-code">{preview.language}</span>
            {showBrands && <Badge tone={preview.brand_id ? 'info' : undefined}>{getBrandName(preview.brand_id)}</Badge>}
            {!preview.is_active && <Badge>{t('channelsUi.notifications.inactive', 'Désactivé')}</Badge>}
          </div>
          <div className="notif-v2__frame">
            <iframe srcDoc={preview.html_content} title={preview.name} sandbox="" />
          </div>
        </Modal>
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>{toast.text}</Toast>
      )}
    </div>
  );
};

export default NotificationsSettings;
